import { BASE_URL } from '@/services/api';
import { platformColors } from '@/composables/usePlatformColor';
import { rememberBootColors } from '@/services/bootColors';

const STYLE_TAG_ID = '__branding_style__';
// Dernières couleurs appliquées, gardées dans le navigateur : au rechargement elles sont posées
// avant le premier affichage, sans attendre l'API (sinon la couleur par défaut clignote).
const CACHE_KEY = 'branding.colors';
const COLOR_KEYS = ['primary_color', 'sidebar_bg_color', 'sidebar_text_color'];

function darken(hex, amount = 15) {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    r = Math.max(0, r - amount);
    g = Math.max(0, g - amount);
    b = Math.max(0, b - amount);
    return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function hexToRgb(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
}

/**
 * Applique les couleurs d'une boutique au back-office. Ce qu'elle ne définit pas vient des
 * couleurs par défaut de la plateforme (Landlord → Intégrations) ; applyBranding({}) applique
 * donc ces seules couleurs par défaut.
 */
export function applyBranding(branding) {
    if (!branding) return;

    // couleurs propres à la boutique : gardées pour le prochain démarrage (sinon rien à garder)
    if (COLOR_KEYS.some(key => branding[key])) {
        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(COLOR_KEYS.map(key => [key, branding[key] || null]))));
        } catch (e) { /* stockage indisponible */ }
    } else {
        forgetCachedBranding();
    }

    const platform = platformColors();
    const primary = branding.primary_color || platform.primary_color;
    const sidebarBg = branding.sidebar_bg_color;
    const sidebarText = branding.sidebar_text_color || platform.sidebar_text_color;

    // on repart des couleurs d'origine du thème ; s'il n'y a rien à appliquer, on s'arrête là
    removeAppliedBranding();
    if (!primary && !sidebarBg && !sidebarText) {
        rememberBootColors(BOOT_KEY, STYLE_TAG_ID, BOOT_VARS, '');
        return;
    }

    const root = document.documentElement;
    if (primary) {
        const dark = darken(primary);
        const rgb = hexToRgb(primary);
        // Bootstrap / KeenThemes CSS variables
        root.style.setProperty('--bs-primary', primary);
        root.style.setProperty('--bs-primary-rgb', rgb);
        root.style.setProperty('--kt-primary', primary);
        root.style.setProperty('--kt-primary-rgb', rgb);
        root.style.setProperty('--kt-primary-active', dark);
        root.style.setProperty('--kt-primary-light', `rgba(${rgb}, 0.1)`);
        root.style.setProperty('--bs-primary-light', `rgba(${rgb}, 0.1)`);
    }

    // Remove and rebuild injected style
    const existing = document.getElementById(STYLE_TAG_ID);
    if (existing) existing.remove();

    const rules = [];

    if (primary) {
        const dark = darken(primary);
        const rgb = hexToRgb(primary);
        const light = `rgba(${rgb}, 0.1)`;
        const medium = `rgba(${rgb}, 0.2)`;
        rules.push(`
            .btn-primary, .btn-primary.disabled, .btn-primary:disabled {
                background-color: ${primary} !important;
                border-color: ${primary} !important;
                color: #fff !important;
            }
            .btn-primary:hover, .btn-primary:focus, .btn-primary:active, .btn-primary.active,
            .show > .btn-primary.dropdown-toggle {
                background-color: ${dark} !important;
                border-color: ${dark} !important;
                color: #fff !important;
            }

            /* btn-light — hover/active hardcodé #0b3d87 dans style.bundle.css */
            .btn-check:active+.btn.btn-light,
            .btn-check:checked+.btn.btn-light,
            .btn.btn-light.active,
            .btn.btn-light.show,
            .btn.btn-light:active:not(.btn-active),
            .btn.btn-light:focus:not(.btn-active),
            .btn.btn-light:hover:not(.btn-active),
            .show>.btn.btn-light {
                border-color: ${primary} !important;
                background-color: ${primary} !important;
                color: #fff !important;
            }
            .btn.btn-light:hover:not(.btn-active) *,
            .btn.btn-light:active:not(.btn-active) *,
            .btn.btn-light.active *,
            .btn.btn-light.show * {
                color: #fff !important;
            }

            /* btn-light-primary */
            .btn-light-primary { color: ${primary} !important; }
            .btn-light-primary:hover, .btn-light-primary:active, .btn-light-primary.active {
                background-color: ${primary} !important; color: #fff !important;
            }

            /* btn-active-light-primary (Metronic) — fond clair au hover, texte primary */
            .btn-active-light-primary:hover:not(.btn-active),
            .btn-active-light-primary:focus:not(.btn-active) {
                background-color: ${light} !important;
                color: ${primary} !important;
            }
            .btn-active-light-primary.active,
            .btn-active-light-primary:active {
                background-color: ${light} !important;
                color: ${primary} !important;
            }

            /* btn-active-color-primary — uniquement la couleur du texte/icône change */
            .btn-active-color-primary:hover,
            .btn-active-color-primary:focus,
            .btn-active-color-primary:active,
            .btn-active-color-primary.active { color: ${primary} !important; }

            /* Général */
            .text-primary { color: ${primary} !important; }
            .bg-primary { background-color: ${primary} !important; }
            .border-primary { border-color: ${primary} !important; }
            .badge-light-primary { color: ${primary} !important; background-color: ${light} !important; }
            .nav-line-tabs .nav-item .nav-link.active { border-bottom-color: ${primary} !important; color: ${primary} !important; }
            .menu-state-primary .menu-item.active > .menu-link .menu-title { color: ${primary} !important; }
            .svg-icon-primary { color: ${primary} !important; }
            a:not(.btn):not(.header-btn-pos):not(.footer-icon-btn):hover { color: ${primary} !important; }
            .page-item.active .page-link { background-color: ${primary} !important; border-color: ${primary} !important; color: #fff !important; }
            .form-check-input:checked { background-color: ${primary} !important; border-color: ${primary} !important; }
            .aside-menu .menu-item.active > .menu-link .menu-title { color: ${primary} !important; }
            .aside-menu .menu-item.active > .menu-link .menu-icon i { color: ${primary} !important; }

            /* Sous-menu des pages Paramètres (BaseSettingXxxLayout, 5 fichiers,
               chacun avec son propre .setting-link.active codé en dur) */
            .setting-link.active { background-color: ${light} !important; }
            .setting-link.active span,
            .setting-link:hover span { color: ${primary} !important; }

            /* Daterangepicker — body prefix pour passer après plugins.bundle.css */
            body .daterangepicker td.active,
            body .daterangepicker td.active:hover {
                background-color: ${primary} !important;
                border-color: transparent !important;
                color: #fff !important;
            }
            body .daterangepicker td.in-range {
                background-color: ${medium} !important;
                color: #000 !important;
            }
            body .daterangepicker td.available:hover,
            body .daterangepicker th.available:hover {
                background-color: ${light} !important;
                color: ${primary} !important;
            }
            body .daterangepicker .ranges ul li.active {
                background-color: ${primary} !important;
                color: #fff !important;
            }
            body .daterangepicker .ranges ul li:hover {
                background-color: ${primary} !important;
                color: #fff !important;
            }
            body .daterangepicker .drp-buttons .applyBtn {
                background-color: ${primary} !important;
                border-color: ${primary} !important;
                color: #fff !important;
            }

            /* Flatpickr */
            body .flatpickr-day.selected,
            body .flatpickr-day.selected:hover,
            body .flatpickr-day.startRange,
            body .flatpickr-day.startRange:hover,
            body .flatpickr-day.endRange,
            body .flatpickr-day.endRange:hover {
                background: ${primary} !important;
                border-color: ${primary} !important;
                color: #fff !important;
            }
            body .flatpickr-day.inRange {
                background: ${medium} !important;
                border-color: ${medium} !important;
            }
        `);
    }

    if (sidebarBg) {
        rules.push(`
            #kt_aside { background-color: ${sidebarBg} !important; }
            .aside { background-color: ${sidebarBg} !important; }
        `);
    }

    if (sidebarText) {
        rules.push(`
            .aside-menu .menu-item > .menu-link .menu-title { color: ${sidebarText} !important; }
            .aside-menu .menu-item > .menu-link .menu-icon i { color: ${sidebarText} !important; }
            .aside-menu .menu-item > .menu-link .menu-arrow { color: ${sidebarText} !important; }
            #kt_aside_logo a { color: ${sidebarText} !important; }
            .aside-toggle { color: ${sidebarText} !important; }

            /* Tout le menu suit la couleur du texte choisie : icônes (cadre teinté), entrée
               active et survol, puces des sous-menus — sinon, sur un fond de menu de la couleur
               principale, les icônes (de cette même couleur) disparaissent */
            .aside-menu .menu-icon-wrapper,
            .aside-menu .menu-link.active .menu-icon-wrapper {
                color: ${sidebarText} !important;
                background-color: rgba(${hexToRgb(sidebarText)}, 0.14) !important;
            }
            .aside-menu .menu-link.active .menu-title { color: ${sidebarText} !important; }
            .aside-menu .menu-link:hover { background-color: rgba(${hexToRgb(sidebarText)}, 0.08) !important; }
            .aside-menu .menu-link.active { background-color: rgba(${hexToRgb(sidebarText)}, 0.14) !important; }
            .aside-menu .menu-link.active::before { background-color: ${sidebarText} !important; }
            .aside-menu .menu-bullet .bullet-dot { background-color: rgba(${hexToRgb(sidebarText)}, 0.55) !important; }
            .aside-menu .menu-sub-accordion .menu-link:hover .bullet-dot,
            .aside-menu .menu-sub-accordion .menu-link.active .bullet-dot { background-color: ${sidebarText} !important; }
        `);
    }

    if (rules.length) {
        const style = document.createElement('style');
        style.id = STYLE_TAG_ID;
        style.textContent = rules.join('\n');
        document.body.appendChild(style);
    }

    rememberBootColors(BOOT_KEY, STYLE_TAG_ID, BOOT_VARS, rules.join('\n'));
}

// Couleurs gardées sur l'appareil et réappliquées par index.html avant le premier affichage
// (sinon la couleur d'origine du thème apparaît un instant à chaque chargement)
const BOOT_KEY = 'backoffice.boot.branding';
const BOOT_VARS = ['--bs-primary', '--bs-primary-rgb', '--kt-primary', '--kt-primary-rgb', '--kt-primary-active', '--kt-primary-light', '--bs-primary-light'];


// Au démarrage de l'application : couleurs de la dernière visite, en attendant celles de l'API
// (à défaut, les couleurs par défaut de la plateforme)
export function applyCachedBranding() {
    try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        const isColor = (value) => value === null || /^#[0-9a-f]{6}$/i.test(value);
        if (cached && COLOR_KEYS.every(key => isColor(cached[key] ?? null))) {
            applyBranding(cached);
            return;
        }
    } catch (e) { /* cache illisible : couleurs par défaut */ }

    applyBranding({});
}

// Le back-office applique-t-il la personnalisation de la boutique ? (voir LayoutBasic)
export const BACKOFFICE_BRANDING = process.env.VUE_APP_BACKOFFICE_BRANDING === 'true';

// Bleu de la plateforme quand le Landlord n'en définit pas (comme ShopBranding côté serveur)
const DEFAULT_PLATFORM_COLOR = '#0b3d87';

/**
 * Image par défaut (product, bundle, promotion, category) affichée par un écran avant que
 * l'API n'en fournisse une : à la couleur de la boutique quand le back-office est
 * personnalisé, sinon à celle de la plateforme — comme celles que renvoie l'API
 * (ShopBranding côté serveur). La couleur est toujours dans l'adresse : l'image est
 * gardée un an par le navigateur.
 */
export function brandedPlaceholder(type) {
    const isHex = (value) => /^#[0-9a-f]{6}$/i.test(value || '');
    let color = null;
    if (BACKOFFICE_BRANDING) {
        try {
            color = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')?.primary_color || null;
        } catch (e) { /* cache illisible : couleur de la plateforme */ }
    }
    if (!isHex(color)) color = platformColors().primary_color;
    if (!isHex(color)) color = DEFAULT_PLATFORM_COLOR;

    return BASE_URL + '/placeholder/' + type + '.svg?color=' + color.slice(1).toLowerCase();
}

// Changement de boutique : ses couleurs ne sont pas celles gardées, on repart de l'API
export function forgetCachedBranding() {
    try { localStorage.removeItem(CACHE_KEY); } catch (e) { /* stockage indisponible */ }
}

// Déconnexion : plus aucune couleur de boutique, ni gardée ni appliquée
export function clearBranding() {
    forgetCachedBranding();
    removeAppliedBranding();
}

function removeAppliedBranding() {
    const existing = document.getElementById(STYLE_TAG_ID);
    if (existing) existing.remove();

    const root = document.documentElement;
    [
        '--bs-primary', '--bs-primary-rgb', '--kt-primary', '--kt-primary-rgb',
        '--kt-primary-active', '--kt-primary-light', '--bs-primary-light',
    ].forEach(prop => root.style.removeProperty(prop));
}
